import React, { useState, useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

// —— Netflix-ish tokens (visual only)
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  text: "#ffffff",
  muted: "rgba(255,255,255,0.75)",
  border: "rgba(255,255,255,0.12)",
  red: "#E50914",
};

interface Booking {
  date: string;
  session: string;
  client: string;
  chips: number;
  duration?: string;
}

interface TalentWalletCardProps {
  earnedChips: number;
  isLoading: boolean;
  onSync: (amount?: number) => Promise<{ success: boolean; message?: string }>;
  bookings?: Booking[];
}

const TalentWalletCard: React.FC<TalentWalletCardProps> = ({
  earnedChips: initialEarnedChips,
  isLoading,
  onSync,
  bookings = [],
}) => {
  const [earnedChips, setEarnedChips] = useState(initialEarnedChips);
  const [syncSuccess, setSyncSuccess] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  const totalSessions = bookings.length;
  const avgChips = useMemo(
    () => (totalSessions ? Math.round(earnedChips / totalSessions) : 0),
    [earnedChips, totalSessions]
  );

  const totalDuration = useMemo(() => {
    return bookings.reduce((sum, b) => {
      const minutes = parseInt(b.duration || "0", 10);
      return sum + (isNaN(minutes) ? 0 : minutes);
    }, 0);
  }, [bookings]);

  return (
    <>
      <Card
        elevation={0}
        sx={{
          mb: 4,
          p: { xs: 3, md: 4 },
          borderRadius: 4,
          backgroundColor: COLORS.panel,
          border: `1px solid ${COLORS.border}`,
          color: COLORS.text,
          boxShadow: "0 16px 48px rgba(0,0,0,0.5)",
        }}
      >
        <CardContent>
          <Box
            display="flex"
            flexDirection={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            gap={4}
          >
            {/* Left */}
            <Box flex={1}>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                sx={{ color: COLORS.muted }}
                gutterBottom
              >
                Chips Earned
              </Typography>
              <Typography
                variant="h2"
                fontWeight={700}
                sx={{ fontSize: "3rem", mb: 1, color: COLORS.text }}
              >
                {initialEarnedChips}
              </Typography>
              <Typography variant="body2" sx={{ color: COLORS.muted }} mb={3}>
                This amount is tied to this talent profile.
              </Typography>
            </Box>

            {/* Right */}
            <Box
              flex={1}
              sx={{
                p: 3,
                borderRadius: 3,
                border: `1px solid ${COLORS.border}`,
                backgroundColor: "rgba(255,255,255,0.06)",
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ color: COLORS.text }}
                gutterBottom
              >
                📊 Wallet Insights
              </Typography>

              {[
                {
                  icon: <CalendarMonthIcon fontSize="small" color="primary" />,
                  label: "Total Sessions",
                  value: totalSessions,
                },
                {
                  icon: <LeaderboardIcon fontSize="small" color="primary" />,
                  label: "Avg Chips / Session",
                  value: avgChips,
                },
                {
                  icon: <AccessTimeIcon fontSize="small" color="primary" />,
                  label: "Total Duration",
                  value: `${totalDuration} min`,
                },
              ].map(({ icon, label, value }, idx) => (
                <Box
                  key={idx}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  py={1.2}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    {icon}
                    <Typography variant="body2" sx={{ color: COLORS.muted }}>
                      {label}
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={600} sx={{ color: COLORS.text }}>
                    {value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={syncSuccess}
        autoHideDuration={3000}
        onClose={() => setSyncSuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSyncSuccess(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          icon={
            snackbarSeverity === "success" ? (
              <CheckCircleOutlineIcon />
            ) : undefined
          }
        >
          {snackbarMessage ||
            "Chips successfully transferred to your main wallet!"}
        </Alert>
      </Snackbar>
    </>
  );
};
export default TalentWalletCard;
